import type { AnalyticsRawData, DateWindow } from "@/analytics/core/types";
import type { DepartmentDefinition } from "@/grid/core/types";
import { supabase } from "@/lib/supabase";

const PAGE_SIZE = 1000;

async function fetchAll<T>(
  fetchPage: (from: number, to: number) => Promise<{ data: T[] | null; error: Error | null }>,
): Promise<T[]> {
  const rows: T[] = [];
  let from = 0;

  while (true) {
    const to = from + PAGE_SIZE - 1;
    const { data, error } = await fetchPage(from, to);
    if (error) throw error;
    if (!data || data.length === 0) break;
    rows.push(...data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return rows;
}

export async function fetchAnalyticsRawData(
  department: DepartmentDefinition,
  window: DateWindow,
): Promise<AnalyticsRawData> {
  const [teams, reps, dailyActivities, teamPeriodMetrics, repPeriodMetrics] = await Promise.all([
    fetchAll(async (from, to) => {
      const { data, error } = await supabase
        .from("teams")
        .select("id, name, region, department, syncore_ref")
        .eq("active", true)
        .eq("department", department.id)
        .order("name")
        .range(from, to);
      return {
        data:
          data
            ?.filter(
              (
                row,
              ): row is typeof row & {
                id: number;
                name: string;
                region: string;
                department: string;
                syncore_ref: string | null;
              } =>
                row.id != null && row.name != null && row.region != null && row.department != null,
            )
            .map((row) => ({
              id: row.id,
              name: row.name,
              region: row.region,
              department: row.department,
              syncore_ref: row.syncore_ref,
            })) ?? null,
        error: error as Error | null,
      };
    }),
    fetchAll(async (from, to) => {
      const { data, error } = await supabase
        .from("reps")
        .select("id, team_id, full_name, teams!inner(department)")
        .eq("active", true)
        .eq("teams.department", department.id)
        .order("full_name")
        .range(from, to);
      return {
        data:
          data
            ?.filter(
              (row): row is typeof row & { id: number; team_id: number; full_name: string } =>
                row.id != null && row.team_id != null && row.full_name != null,
            )
            .map((row) => ({
              id: row.id,
              team_id: row.team_id,
              full_name: row.full_name,
            })) ?? null,
        error: error as Error | null,
      };
    }),
    fetchAll(async (from, to) => {
      const { data, error } = await supabase
        .from("daily_activities")
        .select(
          `
          rep_id,
          activity_date,
          total_calls,
          connected_calls,
          meetings_held,
          sequences,
          reps!inner (
            teams!inner (department)
          )
        `,
        )
        .eq("reps.teams.department", department.id)
        .gte("activity_date", window.from)
        .lte("activity_date", window.to)
        .range(from, to);
      return {
        data:
          data?.map((row) => ({
            rep_id: row.rep_id as number,
            activity_date: row.activity_date as string,
            total_calls: row.total_calls,
            connected_calls: row.connected_calls,
            meetings_held: row.meetings_held,
            sequences: row.sequences,
          })) ?? null,
        error: error as Error | null,
      };
    }),
    fetchAll(async (from, to) => {
      const { data, error } = await supabase
        .from("team_period_metrics")
        .select(
          `
          team_id,
          period_type,
          period_start,
          sales_amount,
          opps_generated_count,
          opps_generated_value,
          teams!inner (department)
        `,
        )
        .eq("teams.department", department.id)
        .gte("period_start", window.from.slice(0, 8) + "01")
        .lte("period_start", window.to)
        .in("period_type", ["week", "month"])
        .range(from, to);
      return {
        data:
          data?.map((row) => ({
            team_id: row.team_id as number,
            period_type: row.period_type as string,
            period_start: row.period_start as string,
            sales_amount: row.sales_amount,
            opps_generated_count: row.opps_generated_count,
            opps_generated_value: row.opps_generated_value,
          })) ?? null,
        error: error as Error | null,
      };
    }),
    fetchAll(async (from, to) => {
      const { data, error } = await supabase
        .from("period_metrics")
        .select(
          `
          rep_id,
          period_type,
          period_start,
          sales_amount,
          opps_generated_count,
          opps_generated_value,
          sqls,
          reps!inner (
            teams!inner (department)
          )
        `,
        )
        .eq("reps.teams.department", department.id)
        .gte("period_start", window.from.slice(0, 8) + "01")
        .lte("period_start", window.to)
        .in("period_type", ["week", "month"])
        .range(from, to);
      return {
        data:
          data?.map((row) => ({
            rep_id: row.rep_id as number,
            period_type: row.period_type as string,
            period_start: row.period_start as string,
            sales_amount: row.sales_amount,
            opps_generated_count: row.opps_generated_count,
            opps_generated_value: row.opps_generated_value,
            sqls: row.sqls as number | null,
          })) ?? null,
        error: error as Error | null,
      };
    }),
  ]);

  return { teams, reps, dailyActivities, teamPeriodMetrics, repPeriodMetrics };
}
